import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import {
  backOfficeAccess,
  useBackOfficeAccess,
} from "@/features/auth/back-office-access";
import { useUser } from "@/features/auth/hooks/use-user";
import { useTitle } from "@/features/utils/use-title";
import { ArrowLeft } from "@phosphor-icons/react";
import { Navigate, Outlet, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminSidebar } from "./sidebar";
import { GlobalSearchTrigger } from "./components/global-search";

/**
 * The platform back-office — its own application, not a tab of the tenant app.
 *
 * Everything under `/admin` is cross-tenant and staff-only, and it stopped
 * fitting inside the tenant back-office the moment it grew a data explorer: the
 * tenant sidebar is about one company, this one is about all of them, and the
 * two menus were competing for the same rail. So this layout owns its sidebar,
 * its header and its own search bar.
 *
 * The route guard is duplicated here on purpose. The router only builds these
 * routes for staff, but that decision is made from a cached profile; this
 * re-check is what makes a stale cache a redirect rather than a blank page full
 * of failing requests. The real authorisation lives on the server
 * (`checkRole("SYSADMIN")` on every admin route, `checkStaffRead()` on the few
 * the read-only account may also call).
 */
export const AdminLayout = () => {
  const { data: user, isLoading } = useUser();

  if (isLoading) return null;
  if (!backOfficeAccess(user?.role)) return <Navigate to="/" replace />;

  return (
    <SidebarProvider
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 60)",
          "--header-height": "calc(var(--spacing) * 14)",
        } as React.CSSProperties
      }
    >
      <AdminSidebar variant="inset" />
      <SidebarInset>
        <AdminHeader />
        <div className="flex flex-1 flex-col">
          <div className="@container/main flex flex-1 flex-col gap-2">
            <div className="px-4 md:px-8 pb-12">
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

/**
 * Always visible, unlike the tenant app's header: it carries the cross-entity
 * search, which is the back-office's main way of getting anywhere.
 */
function AdminHeader() {
  const title = useTitle();
  const access = useBackOfficeAccess();

  return (
    <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur sticky top-0 z-20">
      <div className="flex w-full items-center gap-2 px-4 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-1 data-[orientation=vertical]:h-4"
        />
        <h1 className="text-base font-medium truncate hidden md:block">
          {title}
        </h1>
        <div className="flex-1" />
        {/* The search reads every table, as the explorer does: SYSADMIN only. */}
        {access === "admin" && <GlobalSearchTrigger />}
      </div>
    </header>
  );
}

/**
 * The pages of the back-office the read-only account (sales) is not given —
 * technical ones, the explorer, anything that writes — as a layout route: a
 * link or a typed URL lands it on the control centre instead of on a page whose
 * every request the server refuses. Not the authorisation, which stays on the
 * server; the menu (`adminSectionsFor`) does not offer them either.
 */
export const AdminOnly = () => {
  const access = useBackOfficeAccess();
  if (access !== "admin") return <Navigate to="/admin/live" replace />;
  return <Outlet />;
};

/**
 * Page frame of the back-office. Wider than the tenant app's `PageLayout` —
 * these pages are tables with thirty columns, not forms.
 */
export function AdminPageLayout({
  title,
  description,
  actions,
  backTo,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  backTo?: { label: string; path: string };
  children: React.ReactNode;
}) {
  useTitle(title);
  const navigate = useNavigate();

  return (
    <div className="space-y-6 mt-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1 min-w-0">
          {backTo && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(backTo.path)}
              className="mb-1 -ml-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {backTo.label}
            </Button>
          )}
          <h2 className="text-2xl font-bold tracking-tight truncate">
            {title}
          </h2>
          {description && (
            <div className="text-muted-foreground text-sm">{description}</div>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      {children}
    </div>
  );
}
